
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { UseFetchEnabledParams, UseMutationsParams, UseUpdateParams } from "@/shared/interfaces";
import { urlBackend } from "@/shared/constants";
import { erpApi } from "@/shared/axios";
import { getUrlParamsFetch } from "@/shared/utils";
import { handleAxiosError } from "@/shared/utils/general";
import { Auditorialog, AuditorialogForm, AuditorialogPagingResp, AuditorialogParams } from "@/erp/auditoria/auditorialog";

const { get, post, put } = erpApi();

export type AuditorialogExcelTask = {
  task_id: string;
  state: string;
  ready?: boolean;
  progress?: number;
  message?: string;
  current?: number;
  total?: number;
  filename?: string;
  download_url?: string;
  task_status?: string;
  error?: string;
};

// get

export const useFetchGetAuditorialog = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<AuditorialogParams> = {}) => {
  return useQuery({
    queryKey: ['auditorialog', ...Object.values(params ?? {})],
    queryFn: () => getAuditorialog(params),
    enabled: enabled,
    refetchOnWindowFocus: false,
  });
};

export const getAuditorialog = async (
  params?: AuditorialogParams
) => {
  const queryParams = getUrlParamsFetch(params ?? {});
  return get<AuditorialogPagingResp>(`${urlBackend}api/v1/auditorialog/?${queryParams}`, true);
};

export const createAuditorialogExcelAsync = async (params: Record<string, unknown>) => {
  return post<AuditorialogExcelTask>(
    `${urlBackend}api/v1/crear-excel-auditorialog-async/`,
    params,
    true
  );
};

export const getAuditorialogExcelStatus = async (taskId: string) => {
  return get<AuditorialogExcelTask>(
    `${urlBackend}api/v1/estado-excel-auditorialog/${taskId}/`,
    true
  );
};

// create

export const useFetchPostAuditorialog = ({
  returnUrl,
  navigate
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AuditorialogForm) => {
      return postAuditorialog(data)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['auditorialog']
      });

      navigate && returnUrl && navigate(returnUrl);

      toast.success(data.message);

    },
    onError: ((error) => {
      handleAxiosError(error);
    }),
    onSettled: () => {

    },
  });
};

export const postAuditorialog = async (data: AuditorialogForm) => {
  return await post<Auditorialog>(`${urlBackend}api/v1/auditorialog/`, data, true);
};

// filter by id

export const useFetchGetByIdAuditorialog = (id: string) => {
  return useQuery({
    queryKey: ['auditorialog', id],
    queryFn: async () => {
      return getByIdAuditorialog(id);
    },
    refetchOnWindowFocus: false,
  });
};

export const getByIdAuditorialog = async (id: string) => {
  const res = await get<Auditorialog>(`${urlBackend}api/v1/auditorialog/${id}`, true);
  return res;
};

// update

export const useFetchPutAuditorialog = ({
  navigate,
  returnUrl
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UseUpdateParams<AuditorialogForm>) => {
      return putAuditorialog(data);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: ['auditorialog']
      });
      
      navigate && returnUrl && navigate(returnUrl);

      toast.success(res.message);
    },
    onError: (error) => {
      handleAxiosError(error);
    },
    onSettled: () => {

    },
  });
};

export const putAuditorialog = async ({
  id,
  data
}: UseUpdateParams<AuditorialogForm>) => {
  return put<Auditorialog>(`${urlBackend}api/v1/auditorialog/${id}/`, data, true);
};
